// ─── Types ────────────────────────────────────────────────────────────────────

export interface HelpSection {
  icon: string
  heading: string
  body: string
  element?: string        // data-tour selector; if present, shows "Show me" button
  mobileElement?: string  // alternative selector used when viewport width < 768px
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
}

export interface RouteContent {
  title: string
  intro: string        // subtitle shown in the intro step
  sections: HelpSection[]
}

// ─── Admin Portal ─────────────────────────────────────────────────────────────

const adminOverview: RouteContent = {
  title: 'Overview',
  intro: 'Your admin command center, a live snapshot of your wholesale business.',
  sections: [
    {
      icon: 'bi-speedometer2',
      heading: 'Your live dashboard',
      body: 'Customer counts, order totals, and any issues requiring attention are all surfaced here at a glance.',
      element: '[data-tour="overview-stats"]',
      side: 'bottom',
    },
    {
      icon: 'bi-shield-exclamation',
      heading: 'Issues & Alerts',
      body: 'This panel flags anything that needs attention like pending orders, unread messages and accounts on hold. Each alert links straight to where you need to act.',
      element: '[data-tour="overview-issues"]',
      side: 'top',
    },
    {
      icon: 'bi-lightning-charge',
      heading: 'Quick Actions',
      body: 'Shortcuts to Customers, Orders, Catalog, and Featured Products. Jump straight in without navigating the tabs.',
      element: '[data-tour="overview-quicklinks"]',
      side: 'top',
    },
    {
      icon: 'bi-grid',
      heading: 'Your portal navigation',
      body: 'Everything is here; Catalog, Customers, Orders, Notifications, Storefront, and Settings. Storefront has five sub-pages: Branding, Landing, About, Contact, and Products. Start with Catalog to build your product lineup before onboarding customers.',
      element: '[data-tour="admin-nav"]',
      side: 'bottom',
    },
  ],
}

const adminCatalogProducts: RouteContent = {
  title: 'Products',
  intro: 'Your full product lineup. Import a spreadsheet to get live fast, or build manually one at a time.',
  sections: [
    {
      icon: 'bi-file-earmark-spreadsheet',
      heading: 'CSV Import Wizard (Recommended)',
      body: 'Got a spreadsheet? Upload it here. The wizard reads your column headers and lets you map them to our fields, no reformatting required. SKU, Name, and Price are the only required columns. Everything else imports as custom attributes. Most wholesalers are fully live in under 10 minutes.',
      element: '[data-tour="catalog-import-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-plus-circle',
      heading: 'Add a product manually',
      body: 'Prefer to build one at a time? Add a single product with name, SKU, price, category, manufacturer, description, images, and any custom specs.',
      element: '[data-tour="catalog-add-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-table',
      heading: 'Your full product list',
      body: 'Search by name or SKU, filter by category or manufacturer, sort any column. Click a row to open the product detail and edit anything.',
      element: '[data-tour="catalog-table"]',
      side: 'top',
    },
    {
      icon: 'bi-grid',
      heading: 'More catalog sections',
      body: 'Use the tabs above to navigate Categories, Manufacturers, Literature, and Featured Products.',
      element: '[data-tour="catalog-tabs"]',
      side: 'bottom',
    },
  ],
}

const adminCatalogCategories: RouteContent = {
  title: 'Categories',
  intro: 'Organize your products into categories so customers can filter and browse easily.',
  sections: [
    {
      icon: 'bi-plus-circle',
      heading: 'Add a category',
      body: 'Create a new category with a name and optional image. Categories appear as filter options in the customer catalog.',
      element: '[data-tour="categories-add-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-table',
      heading: 'Your category list',
      body: 'Click any row to edit the name or image. Deleting a category doesn\'t delete its products, they just become uncategorized.',
      element: '[data-tour="categories-table"]',
      side: 'top',
    },
    {
      icon: 'bi-lightbulb',
      heading: 'Build these first',
      body: 'Set up your categories before importing products so you can assign them during the CSV import. You can always add more later.',
    },
  ],
}

const adminCatalogManufacturers: RouteContent = {
  title: 'Manufacturers',
  intro: 'The brands behind your products. Logos appear in the storefront manufacturer slider.',
  sections: [
    {
      icon: 'bi-plus-circle',
      heading: 'Add a manufacturer',
      body: 'Add a brand name and logo. The logo appears in the scrolling manufacturer strip on your storefront homepage if that section is enabled.',
      element: '[data-tour="manufacturers-add-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-table',
      heading: 'Your manufacturer list',
      body: 'Click any row to edit the name or upload a new logo. Removing a manufacturer doesn\'t affect the products assigned to it.',
      element: '[data-tour="manufacturers-table"]',
      side: 'top',
    },
    {
      icon: 'bi-building',
      heading: 'Storefront slider',
      body: 'Head to Storefront → Landing or Storefront → Products to toggle the manufacturer logo strip on or off per page. Logos are pulled directly from this list.',
    },
  ],
}

const adminCatalogLiterature: RouteContent = {
  title: 'Literature',
  intro: 'Product flyers, spec sheets, and PDFs your customers can download straight from the product page.',
  sections: [
    {
      icon: 'bi-plus-circle',
      heading: 'Add a document',
      body: 'Upload a PDF and link it to a product. It will appear as a downloadable attachment on that product\'s flyer in the customer catalog.',
      element: '[data-tour="literature-add-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-table',
      heading: 'Your document list',
      body: 'Click any row to update the title, replace the file, or change which product it\'s linked to.',
      element: '[data-tour="literature-table"]',
      side: 'top',
    },
  ],
}

const adminCatalogFeatured: RouteContent = {
  title: 'Featured Products',
  intro: 'Ten spotlight slots shown in a carousel on your storefront. Drag to reorder, click to swap products.',
  sections: [
    {
      icon: 'bi-star',
      heading: 'Featured product slots',
      body: 'You have 10 slots. Each filled slot shows a product card in the storefront carousel. Empty slots are hidden from customers.',
      element: '[data-tour="featured-slots"]',
      side: 'top',
    },
    {
      icon: 'bi-arrows-move',
      heading: 'Drag to reorder',
      body: 'Grab any card by its drag handle and drop it into a new position. The order updates live on your storefront.',
    },
    {
      icon: 'bi-pencil',
      heading: 'Swap or remove',
      body: 'Click the edit icon on any slot to pick a different product or clear the slot entirely.',
    },
  ],
}

const adminNotifications: RouteContent = {
  title: 'Notifications',
  intro: 'All activity across your portal in one feed',
  sections: [
    {
      icon: 'bi-bell',
      heading: 'Your notification feed',
      body: 'Every order placed, message sent, and account change appears here. Click any notification to jump straight to the related record.',
      element: '[data-tour="admin-notifications-list"]',
      side: 'top',
    },
    {
      icon: 'bi-funnel',
      heading: 'Filter by unread',
      body: 'Switch between All and Unread to focus on what still needs your attention.',
      element: '[data-tour="admin-notifications-filter"]',
      side: 'bottom',
    },
    {
      icon: 'bi-check2-all',
      heading: 'Mark all as read',
      body: 'Clear your entire unread count at once with the "Mark all read" button.',
    },
  ],
}

const adminCustomers: RouteContent = {
  title: 'Customers',
  intro: 'Your wholesale customer directory, every account that can log in and place orders.',
  sections: [
    {
      icon: 'bi-person-plus',
      heading: 'Onboard a new customer',
      body: 'Create an account with contact details, pricing tier, and credit limit. An invitation email goes out automatically.',
      element: '[data-tour="customers-add-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-table',
      heading: 'Your full customer list',
      body: 'Search by name, email, or company. Click any row to open the customer detail; edit profile, adjust credit limits, view order history, or change account status.',
      element: '[data-tour="customers-table"]',
      side: 'top',
    },
    {
      icon: 'bi-currency-dollar',
      heading: 'Credit limits',
      body: 'Set a dollar credit limit per customer, they can\'t place orders that exceed their available credit. Leave blank for unlimited. Customers see their live credit balance in their portal.',
    },
    {
      icon: 'bi-toggles',
      heading: 'Account statuses',
      body: 'Active customers can log in and place orders. Invited means the invite was sent but they haven\'t logged in yet. Deactivated and Suspended block access entirely. Credit Hold lets them log in but blocks order placement.',
    },
  ],
}

const adminOrders: RouteContent = {
  title: 'Orders',
  intro: 'Every order placed through the customer portal lands here. New orders come in as Pending and need your review.',
  sections: [
    {
      icon: 'bi-receipt',
      heading: 'All your orders in one place',
      body: 'Search by order number or customer, filter by status, click any row to open the full order detail; line items, totals, status controls, and messaging.',
      element: '[data-tour="orders-table"]',
      side: 'top',
    },
    {
      icon: 'bi-funnel',
      heading: 'Filter by status',
      body: 'Drill into just the Pending orders that need attention, or view Shipped orders to track what\'s in transit.',
      element: '[data-tour="orders-filter"]',
      side: 'bottom',
    },
    {
      icon: 'bi-check2-square',
      heading: 'Bulk status updates',
      body: 'Select multiple orders and update them all at once, great for marking a batch as Shipped after a pickup day.',
      element: '[data-tour="orders-bulk"]',
      side: 'bottom',
    },
    {
      icon: 'bi-chat-dots',
      heading: 'Messaging',
      body: 'Each order has a Messages tab. Communicate directly with the customer; share tracking info, flag issues, answer questions. Messages appear in real time in their portal.',
    },
  ],
}

const adminStorefrontBranding: RouteContent = {
  title: 'Branding',
  intro: 'Your brand identity and display settings. Everything here shapes how your storefront looks and feels.',
  sections: [
    {
      icon: 'bi-building',
      heading: 'Identity',
      body: 'Your company name, tagline, logos, color themes, contact info, and social links. The site theme drives your storefront colors; the document theme controls invoices and outgoing emails. Click Edit to update any of it.',
      element: '[data-tour="branding-identity"]',
      side: 'bottom',
    },
    {
      icon: 'bi-megaphone',
      heading: 'Notification Banner',
      body: 'The announcement banner that runs across the top of every public page. Write your message, pick a color, and toggle it on or off. Great for promotions, holiday hours, or policy updates.',
      element: '[data-tour="branding-display"]',
      side: 'bottom',
    },
    {
      icon: 'bi-grid',
      heading: 'More storefront pages',
      body: 'Use the tabs above to navigate the other storefront pages. Each one has its own content and settings.',
      element: '[data-tour="storefront-tabs"]',
      side: 'bottom',
    },
  ],
}

const adminStorefrontLanding: RouteContent = {
  title: 'Landing Page',
  intro: 'The homepage your customers see first. Set the hero content, call-to-action buttons, and which sections are shown.',
  sections: [
    {
      icon: 'bi-card-heading',
      heading: 'Hero section',
      body: 'Set the eyebrow label, headline, subheading, and an optional hero logo image. This is the first thing customers see when they arrive.',
    },
    {
      icon: 'bi-cursor',
      heading: 'Call-to-action buttons',
      body: 'Configure two CTA buttons; set the label, icon, and destination path for each. Guide customers toward your catalog or featured products.',
    },
    {
      icon: 'bi-star',
      heading: 'Featured products carousel',
      body: 'Toggle the featured products carousel strip on or off for the landing page. Products shown come from Catalog → Featured Products.',
    },
    {
      icon: 'bi-building',
      heading: 'Manufacturer slider',
      body: 'Toggle the scrolling manufacturer logo strip on or off for the landing page. Logos come from Catalog → Manufacturers.',
    },
  ],
}

const adminStorefrontAbout: RouteContent = {
  title: 'About Page',
  intro: 'Tell your story. Edit the content customers see on your About page.',
  sections: [
    {
      icon: 'bi-info-circle',
      heading: 'About page content',
      body: 'Set the title, subtitle, and body text for your About page. Share your company history, mission, or anything you want customers to know.',
    },
    {
      icon: 'bi-image',
      heading: 'About page image',
      body: 'Upload a photo or image to display alongside your About content. Great for a team photo, facility shot, or brand imagery.',
    },
  ],
}

const adminStorefrontContact: RouteContent = {
  title: 'Contact Page',
  intro: 'Control how your contact details are displayed to customers on the Contact page.',
  sections: [
    {
      icon: 'bi-geo-alt',
      heading: 'Address & contact info',
      body: 'Set the address, phone, and email shown on your Contact page. These also appear on invoices and email footers.',
    },
    {
      icon: 'bi-map',
      heading: 'Contact page layout',
      body: 'Preview how your contact details are presented to customers. Changes here update the public-facing Contact page immediately.',
    },
  ],
}

const adminStorefrontProducts: RouteContent = {
  title: 'Products Page',
  intro: 'Customize the header of your customer-facing product catalog and toggle which sections appear.',
  sections: [
    {
      icon: 'bi-card-heading',
      heading: 'Page header',
      body: 'Set the title and subtitle shown at the top of the customer catalog. Customers see this every time they browse products.',
    },
    {
      icon: 'bi-star',
      heading: 'Featured products carousel',
      body: 'Toggle the featured products carousel on or off for the Products page. Products shown come from Catalog → Featured Products.',
    },
    {
      icon: 'bi-building',
      heading: 'Manufacturer slider',
      body: 'Toggle the scrolling manufacturer logo strip on or off for the Products page. Logos come from Catalog → Manufacturers.',
    },
  ],
}

const adminSettingsAccount: RouteContent = {
  title: 'Account Settings',
  intro: 'Your admin profile and security settings.',
  sections: [
    {
      icon: 'bi-person-gear',
      heading: 'Your account',
      body: 'Update your name, email, and password. These changes only affect your own admin profile.',
      element: '[data-tour="settings-account"]',
      side: 'bottom',
    },
    {
      icon: 'bi-shield-lock',
      heading: 'Two-factor authentication',
      body: 'Add a second layer of security to your admin login. Takes about 60 seconds to set up with any authenticator app.',
      element: '[data-tour="settings-2fa"]',
      side: 'bottom',
    },
    {
      icon: 'bi-bell',
      heading: 'Email notifications',
      body: 'Choose which events trigger an email; new orders, customer messages, account changes, and more. Toggle each one individually.',
      element: '[data-tour="settings-notifications"]',
      side: 'top',
    },
    {
      icon: 'bi-grid',
      heading: 'More settings',
      body: 'Use the tabs to switch between different setting categories. If you don\'t see any other tabs, there are no additional settings available.',
      element: '[data-tour="settings-tabs"]',
      side: 'bottom',
    },
  ],
}

// ─── Customer Portal ──────────────────────────────────────────────────────────

const customerOverview: RouteContent = {
  title: 'Overview',
  intro: 'Your customer dashboard, a quick summary of your account activity.',
  sections: [
    {
      icon: 'bi-bag-check',
      heading: 'Shop the catalog',
      body: 'Browse and order products directly from here. Shop Now takes you straight to the full product catalog.',
      element: '[data-tour="customer-shop-btn"]',
      side: 'bottom',
    },
    {
      icon: 'bi-receipt',
      heading: 'Your order summary',
      body: 'Total orders placed and how many are currently pending. Click Orders to see everything in detail.',
      element: '[data-tour="customer-stats"]',
      side: 'bottom',
    },
    {
      icon: 'bi-grid',
      heading: 'Quick links',
      body: 'Jump straight to your orders, the product catalog, notifications, or account settings.',
      element: '[data-tour="customer-quicklinks"]',
      side: 'top',
    },
    {
      icon: 'bi-grid',
      heading: 'Your portal navigation',
      body: 'Everything is here; Orders, Notifications, and Settings. Head to Orders to track what you\'ve placed, Notifications for updates from the team, and Settings to manage your profile.',
      element: '[data-tour="customer-nav"]',
      side: 'bottom',
    },
  ],
}

const customerOrders: RouteContent = {
  title: 'My Orders',
  intro: 'Every order you\'ve placed through the portal; track status, view line items, message the team.',
  sections: [
    {
      icon: 'bi-search',
      heading: 'Search your orders',
      body: 'Search by product name, SKU, or order number to find exactly what you\'re looking for. You can also click "View order history for this item" on any product to jump straight here with the search pre-filled.',
      element: '[data-tour="customer-orders-search"]',
      side: 'top',
    },
    {
      icon: 'bi-receipt',
      heading: 'Your order history',
      body: 'Click any row to open the full detail; line items, status, tracking, and messages.',
      element: '[data-tour="customer-orders-table"]',
      side: 'top',
    },
    {
      icon: 'bi-funnel',
      heading: 'Filter by status',
      body: 'Narrow down to Pending, Shipped, or any other status to find what you\'re looking for quickly.',
    },
    {
      icon: 'bi-arrow-repeat',
      heading: 'Order statuses',
      body: 'Pending: awaiting review. Shipped: on its way. Delivered: fulfilled. Cancelled: voided.',
    },
    {
      icon: 'bi-chat-dots',
      heading: 'Messaging',
      body: 'Click any order then go to the Messages tab to communicate directly with the team; ask questions, request changes, or get shipping updates.',
    },
  ],
}

const customerNotifications: RouteContent = {
  title: 'Notifications',
  intro: 'Real-time alerts for order updates, messages from the team, and account events.',
  sections: [
    {
      icon: 'bi-bell',
      heading: 'Your notification feed',
      body: 'Order updates, messages from the team, and account events all appear here. Click any notification to see the details and jump to the related order.',
      element: '[data-tour="notifications-list"]',
      side: 'top',
    },
    {
      icon: 'bi-funnel',
      heading: 'Filter notifications',
      body: 'Switch between All and Unread to find what you need quickly.',
      element: '[data-tour="notifications-filter"]',
      side: 'bottom',
    },
    {
      icon: 'bi-check2-all',
      heading: 'Mark all as read',
      body: 'Clear your entire unread count at once with the "Mark all read" button when you have unread notifications.',
    },
  ],
}

const customerSettingsAccount: RouteContent = {
  title: 'Account Settings',
  intro: 'Manage your profile, email address, and password.',
  sections: [
    {
      icon: 'bi-person-gear',
      heading: 'Your profile',
      body: 'Update your name, email, company, and contact details here.',
      element: '[data-tour="account-profile"]',
      side: 'bottom',
    },
    {
      icon: 'bi-key',
      heading: 'Change your password',
      body: 'Enter your current password and a new one to update your login credentials.',
      element: '[data-tour="account-password"]',
      side: 'top',
    },
    {
      icon: 'bi-bell',
      heading: 'Email notifications',
      body: 'Choose which events trigger an email; order updates, messages from the team, and account changes. Toggle each one individually.',
      element: '[data-tour="account-notifications"]',
      side: 'top',
    },
  ],
}

const productsPage: RouteContent = {
  title: 'Products',
  intro: 'Browse the full wholesale catalog, search by SKU, open product flyers, and add items to your cart.',
  sections: [
    {
      icon: 'bi-search',
      heading: 'Search the catalog',
      body: 'Search by product name, SKU, or part number. Results update as you type.',
      element: '[data-tour="products-search"]',
      side: 'bottom',
    },
    {
      icon: 'bi-funnel',
      heading: 'Filter & sort',
      body: 'Narrow by category or manufacturer using the filters. Click any column header to sort.',
      element: '[data-tour="products-filters"]',
      mobileElement: '[data-tour="products-filter-mobile"]',
      side: 'right',
    },
    {
      icon: 'bi-grid',
      heading: 'The product catalog',
      body: 'Click any product to open its flyer; full pricing, description, spec sheets, and downloadable literature. Add to cart directly from the flyer.',
      element: '[data-tour="products-grid"]',
      side: 'top',
    },
  ],
}

// ─── Route Map ────────────────────────────────────────────────────────────────

export const csvImportWizard: RouteContent = {
  title: 'CSV Import Wizard',
  intro: 'Bring your spreadsheet, map any columns to product fields and go live in minutes.',
  sections: [
    {
      icon: 'bi-1-circle',
      heading: 'Prepare your CSV',
      body: 'Export or create a spreadsheet with your product data. Save it as a .CSV file. Every row is one product, column headers go in the first row. You can use any column names you like; you\'ll map them to fields in the next step.',
    },
    {
      icon: 'bi-2-circle',
      heading: 'Upload & auto-detect',
      body: 'Drag your CSV onto the upload zone or click to browse. The wizard reads your headers and detects how many rows are ready to import. Required fields are SKU, Name, and Price.',
    },
    {
      icon: 'bi-3-circle',
      heading: 'Map your columns',
      body: 'Match each product field (Name, SKU, Price, etc.) to the corresponding column in your file. Fields marked with a red * are required. The wizard auto-suggests matches when your column names are close.',
    },
    {
      icon: 'bi-4-circle',
      heading: 'Custom attributes',
      body: 'Any columns you didn\'t map to a standard field can be imported as custom attributes, things like "Bolt Pattern", "Load Rating", or "Finish". These appear on the product flyer and are fully searchable.',
    },
    {
      icon: 'bi-5-circle',
      heading: 'Review & import',
      body: 'Before importing, the wizard shows a live flyer preview and lets you remap any unrecognized category or manufacturer values. New vs. update rows are color-coded. Existing products matched by SKU are updated; new ones are created.',
    },
    {
      icon: 'bi-download',
      heading: 'Don\'t even worry about formatting',
      body: 'The wizard handles all the formatting for you. No need to reformat your spreadsheet, just map the columns and go live. Most wholesalers are fully live in under 10 minutes.',
    },
  ],
}

export const ROUTE_CONTENT: Record<string, RouteContent> = {
  '/admin-portal': adminOverview,
  '/admin-portal/catalog': adminCatalogProducts,
  '/admin-portal/catalog/products': adminCatalogProducts,
  '/admin-portal/catalog/categories': adminCatalogCategories,
  '/admin-portal/catalog/manufacturers': adminCatalogManufacturers,
  '/admin-portal/catalog/literature': adminCatalogLiterature,
  '/admin-portal/catalog/featured-products': adminCatalogFeatured,
  '/admin-portal/notifications': adminNotifications,
  '/admin-portal/customers': adminCustomers,
  '/admin-portal/orders': adminOrders,
  '/admin-portal/storefront': adminStorefrontBranding,
  '/admin-portal/storefront/branding': adminStorefrontBranding,
  '/admin-portal/storefront/landing': adminStorefrontLanding,
  '/admin-portal/storefront/about': adminStorefrontAbout,
  '/admin-portal/storefront/contact': adminStorefrontContact,
  '/admin-portal/storefront/products': adminStorefrontProducts,
  '/admin-portal/settings': adminSettingsAccount,
  '/admin-portal/settings/account': adminSettingsAccount,
  '/customer-portal': customerOverview,
  '/customer-portal/orders': customerOrders,
  '/customer-portal/notifications': customerNotifications,
  '/customer-portal/settings/account': customerSettingsAccount,
  '/products': productsPage,
}

export function getRouteContent(pathname: string): RouteContent | null {
  if (ROUTE_CONTENT[pathname]) return ROUTE_CONTENT[pathname]
  const match = Object.keys(ROUTE_CONTENT)
    .filter(route => pathname.startsWith(route))
    .sort((a, b) => b.length - a.length)[0]
  return match ? ROUTE_CONTENT[match] : null
}

export const tourSeenKey = (pathname: string) => `wsh_tour_seen_${pathname}`
